import React, { useState, useEffect, useRef, useMemo } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import moment from "moment/moment";
import "../pagination/pagination.css";
import Pagination from "../pagination/pagination";
import Select from "react-select";
import { RiRefreshLine } from "react-icons/ri";
import { FaFileCircleExclamation } from "react-icons/fa6";
import LeaseApiService from "../../services/apiService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { ClipLoader } from 'react-spinners';
import { toast } from "react-toastify";
import * as XLSX from "xlsx-js-style";

const boolOptions = [
    { label: "--", value: "" },
    { label: "Yes", value: true },
    { label: "No", value: false },
];

const LeaseMoveInOutReport = () => {
    const apiService = new LeaseApiService();
    const gridRef = useRef();
    const [allRows, setAllRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(false);
    const [FilterTab, setFilterTab] = useState(true);
    const [propertyList, setPropertyList] = useState([]);
    const [unitList, setUnitList] = useState([]);
    const [unitClassList, setUnitClassList] = useState([]);
    const [loader, setLoader] = useState(false);
    const [formData, setFormData] = useState({
        property: { label: "All", value: "All" },
        unitNo: "",
        unitClass: { label: "All", value: "All" },
        moveIn: boolOptions[0],
        moveOut: boolOptions[0],
        flatShifting: boolOptions[0],
        from: "",
        to: "",
    })
    const formatDate = (date) => {
        return moment(date).format("DD-MMM-YYYY");
    };
    useEffect(() => {
        fetchDropdownData();

        const today = new Date();
        const fromDate = new Date(today);
        fromDate.setFullYear(today.getFullYear() - 5);
        const toDate = new Date(today);
        toDate.setFullYear(today.getFullYear() + 5);

        setFormData((prev) => ({
            ...prev,
            from: formatDate(fromDate),
            to: formatDate(toDate),
        }));
    }, []);

    const dateFormatter = (params) => {
        if (!params.value) return '';
        const date = moment(params.value);
        return date.isValid() ? date.format('DD-MMM-YYYY') : '';
    };
    const dateComparator = (date1, date2) => {
        const momentDate1 = moment(date1);
        const momentDate2 = moment(date2);
        return momentDate1.isSame(momentDate2) ? 0 : momentDate1.isBefore(momentDate2) ? -1 : 1;
    };

    const fetchDropdownData = async () => {
        try {
            const params = new URLSearchParams();
            params.append('dropdownTypes', 'LRAgreeSearchProperties,LRSearchUnitClass,');
            const res = await apiService.getApiParamater('REPORT_MULTI_DROP', `?${params.toString()}`);
            const addAllOption = (list) => [{ value: 'All', text: 'All' }, ...list || []];
            setPropertyList(res?.data?.dropdowns?.LRAgreeSearchProperties);
            setUnitClassList(addAllOption(res?.data?.dropdowns?.UnitClass));
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    }

    const getItems = async () => {
        try {
            setLoader(true);
            const today = new Date();
            let todate = formatDate(today);
            const oneYearBack = new Date();
            oneYearBack.setFullYear(today.getFullYear() - 1);
            const updatedValue = formatDate(oneYearBack);
            const params = new URLSearchParams();
            params.append("moveInOutRequest.FromDate", formData?.from || updatedValue);
            params.append("moveInOutRequest.ToDate", formData?.to || todate);
            params.append("moveInOutRequest.PropertyNo", formData?.property?.value?.value || "All");
            params.append("moveInOutRequest.UnitNo", formData?.unitNo?.label || "All");
            params.append("moveInOutRequest.UnitClass", formData?.unitClass?.label || "All");
            if (formData?.moveIn?.value !== "") params.append("moveInOutRequest.MoveIn", formData?.moveIn?.value);
            if (formData?.moveOut?.value !== "") params.append("moveInOutRequest.MoveOut", formData?.moveOut?.value);
            if (formData?.flatShifting?.value !== "") params.append("moveInOutRequest.FlatShifting", formData?.flatShifting?.value);
            const res = await apiService.getApiParamater("REPORT_MOVE_IN_OUT", `?${params.toString()}`);
            const result = res?.data?.result || [];
            if (result?.length) {
                setAllRows(result);
                setCurrentPage(1);
            } else {
                setAllRows([]);
                setCurrentPage(1);
                if (res?.data?.message) toast.error(res.data.message);
            }
            setLoader(false);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching items:", error);
            setLoader(false);
            setLoading(false);
        }
    }
    const columnDefs = [
        { headerName: "Property No", field: "propertyNo", filter: true, floatingFilter: true, suppressMovable: true, width: 130, pinned: "left" },
        { headerName: "Property Name", field: "propertyName", filter: true, floatingFilter: true, suppressMovable: true, width: 200, pinned: "left" },
        { headerName: "Unit No", field: "unitNo", filter: true, floatingFilter: true, suppressMovable: true, width: 110, pinned: "left" },
        { headerName: "Tenant No", field: "tenantNo", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
        { headerName: "Tenant Name", field: "tenantName", filter: true, floatingFilter: true, suppressMovable: true, width: 200 },
        { headerName: "Agent/Direct", field: "agentDirect", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
        { headerName: "Contract Type", field: "contractType", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
        { headerName: "Contract Period", field: "contractPeriod", filter: true, floatingFilter: true, suppressMovable: true, width: 150 },
        { headerName: "Unit Class", field: "unitClass", filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
        { headerName: "Type", field: "type", filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
        {
            headerName: "Actual Start Date", field: "actualStartDate", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
            valueFormatter: dateFormatter,
            comparator: dateComparator,
        },
        {
            headerName: "Lease Date", field: "leaseDate", filter: true, floatingFilter: true, suppressMovable: true, width: 130,
            valueFormatter: dateFormatter,
            comparator: dateComparator,
        },
        {
            headerName: "Vacating Date", field: "vacatingDate", filter: true, floatingFilter: true, suppressMovable: true, width: 140,
            valueFormatter: dateFormatter,
            comparator: dateComparator,
        },
        { headerName: "Finished/Pre-Terminated", field: "finishedPreTerminated", filter: true, floatingFilter: true, suppressMovable: true, width: 190 },
        { headerName: "Remarks", field: "remarks", filter: true, floatingFilter: true, suppressMovable: true, width: 200 },
    ]
    const handleClearFilters = () => {
        setUnitList([]);
        setAllRows([]);
        setCurrentPage(1);
        const today = new Date();
        const fromDate = new Date(today);
        fromDate.setFullYear(today.getFullYear() - 5);
        const toDate = new Date(today);
        toDate.setFullYear(today.getFullYear() + 5);
        setFormData({
            property: { label: "All", value: "All" },
            unitNo: "",
            unitClass: { label: "All", value: "All" },
            moveIn: boolOptions[0],
            moveOut: boolOptions[0],
            flatShifting: boolOptions[0],
            from: formatDate(fromDate),
            to: formatDate(toDate),
        });
    }
    const handleDropDownChange = async ({ name, value }) => {
        if (name === "property") {
            setFormData(prev => ({ ...prev, unitNo: null }));
            const propertyValue = value?.value === 'All' ? null : value?.value;
            const params = new URLSearchParams();
            params.append('DropdownFor', 'LRAgreehUnitNosByPropertyId');
            params.append('CascadValue', propertyValue?.value);
            const res = await apiService.getApiParamater('REPORT_CASCAD', `?${params.toString()}`);
            setUnitList(res?.data?.items || []);
            setFormData(prev => ({ ...prev, [name]: value }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    }
    const handleDateChange = async (input) => {
        const name = input?.target?.name || input.name;
        const value = input?.target?.value || input.value;

        const updatedValue = typeof value === 'string' ? value : formatDate(value);
        let updatedDates = { ...formData, [name]: updatedValue };
        setFormData(updatedDates);
    }
    const normalizeValue = (value) => {
        if (value === null || value === undefined) return "";
        if (value === 0 || value === "0") return "";

        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
            return moment(value).format("DD-MMM-YYYY");
        }

        return value;
    };
    const exportColumns = [
        { header: "Property No", dataKey: "propertyNo" },
        { header: "Property Name", dataKey: "propertyName" },
        { header: "Unit No", dataKey: "unitNo" },
        { header: "Tenant No", dataKey: "tenantNo" },
        { header: "Tenant Name", dataKey: "tenantName" },
        { header: "Agent/Direct", dataKey: "agentDirect" },
        { header: "Contract Type", dataKey: "contractType" },
        { header: "Contract Period", dataKey: "contractPeriod" },
        { header: "Unit Class", dataKey: "unitClass" },
        { header: "Type", dataKey: "type" },
        { header: "Actual Start Date", dataKey: "actualStartDate" },
        { header: "Lease Date", dataKey: "leaseDate" },
        { header: "Vacating Date", dataKey: "vacatingDate" },
        { header: "Finished/Pre-Terminated", dataKey: "finishedPreTerminated" },
        { header: "Remarks", dataKey: "remarks" },
    ];
    const exportPDF = () => {
        if (!allRows?.length) return;

        const doc = new jsPDF("landscape", "pt", "A4");

        doc.setFillColor(110, 81, 153);
        doc.rect(0, 0, doc.internal.pageSize.width, 50, "F");

        doc.setTextColor(255);
        doc.setFontSize(16);
        doc.text("Lease Move In / Move Out Report", 40, 32);

        doc.setFontSize(9);
        doc.text(
            `Period: ${formData?.from} to ${formData?.to}`,
            doc.internal.pageSize.width - 220,
            32
        );

        doc.setTextColor(0);

        doc.autoTable({
            startY: 60,
            head: [exportColumns.map(col => col.header)],
            body: allRows.map(row =>
                exportColumns.map(col => normalizeValue(row[col.dataKey]))
            ),
            styles: {
                fontSize: 8,
                cellPadding: 5,
                valign: "middle",
            },
            headStyles: {
                fillColor: [110, 81, 153],
                textColor: 255,
                fontStyle: "bold",
                halign: "center",
            },
            bodyStyles: {
                halign: "center",
            },
            alternateRowStyles: {
                fillColor: [240, 237, 245],
            },
            didDrawPage: data => {
                doc.setFontSize(8);
                doc.text(
                    `Page ${data.pageNumber}`,
                    doc.internal.pageSize.width / 2,
                    doc.internal.pageSize.height - 10,
                    { align: "center" }
                );
            },
        });
        doc.save("Lease_MoveInOut_Report.pdf");
    };
    const exportExcel = () => {
        if (!allRows?.length) return;

        const title = "LEASE MOVE IN / MOVE OUT REPORT";

        // Reuse the grid's own column definitions (header + formatter) so the
        // export can never drift out of sync with what the grid shows.
        const columns = columnDefs.map(col => ({
            header: col.headerName,
            field: col.field,
            valueFormatter: col.valueFormatter,
        }));

        const dataRows = allRows.map(item =>
            columns.map(col =>
                col.valueFormatter
                    ? col.valueFormatter({ value: item[col.field], data: item })
                    : normalizeValue(item[col.field])
            )
        );

        const worksheet = XLSX.utils.aoa_to_sheet([
            [title],
            columns.map(c => c.header),
            ...dataRows,
        ]);

        // Merge Title
        worksheet["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } },
        ];

        // Auto column width
        worksheet["!cols"] = columns.map((col, i) => {
            const maxLength = Math.max(
                col.header.length,
                ...dataRows.map(r => String(r[i] ?? "").length)
            );
            return { wch: maxLength + 4 };
        });

        // Freeze header
        worksheet["!freeze"] = { ySplit: 2 };

        // Border style with color
        const borderStyle = {
            top: { style: "thin", color: { rgb: "FF000000" } },
            bottom: { style: "thin", color: { rgb: "FF000000" } },
            left: { style: "thin", color: { rgb: "FF000000" } },
            right: { style: "thin", color: { rgb: "FF000000" } },
        };

        // Title style
        const titleCell = worksheet["A1"];
        if (titleCell) {
            titleCell.s = {
                font: { bold: true, sz: 16, color: { rgb: "FF333333" } },
                alignment: { horizontal: "left", vertical: "center" },
                border: borderStyle,
            };
        }

        // Header style
        columns.forEach((_, c) => {
            const cell = worksheet[XLSX.utils.encode_cell({ r: 1, c })];
            if (!cell) return;

            cell.s = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFFFF" } },
                fill: { fgColor: { rgb: "FF6E5199" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: borderStyle,
            };
        });

        // Data cells
        dataRows.forEach((row, r) => {
            columns.forEach((col, c) => {
                const cell = worksheet[XLSX.utils.encode_cell({ r: r + 2, c })];
                if (!cell) return;

                cell.s = {
                    font: { sz: 10, color: { rgb: "FF000000" } },
                    alignment: { vertical: "center", horizontal: "left" },
                    border: borderStyle,
                };
            });
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Move In Move Out");
        XLSX.writeFile(workbook, "Lease_MoveInOut_Report.xlsx");
    };

    const totalPages = Math.max(1, Math.ceil(allRows.length / pageSize));
    const pagination = {
        currentPage,
        totalPages,
        totalRecords: allRows.length,
    };
    const rowData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return allRows.slice(start, start + pageSize);
    }, [allRows, currentPage, pageSize]);
    const changePage = (pageNo) => {
        const clamped = Math.min(Math.max(pageNo, 1), totalPages);
        setCurrentPage(clamped);
    }

    return (
        <div className="p-3">
            <div className="cost-center-section">
                <div
                    className="d-flex mt-3 cursor-pointer slide-header"
                    onClick={() => setFilterTab(!FilterTab)}
                >
                    <div className="h6 pt-1">Search Parameters</div>
                </div>
                <div className={`slide-content ${FilterTab ? "open" : "closed"}`}>
                    <div className={`border-box2 p-3 ${FilterTab ? "slide-in" : "slide-out"}`}>
                        <div className="form-group grid-contain ">
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">Property </label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.property}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'property', value: selectedOption })}
                                    options={propertyList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">Unit No </label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.unitNo}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'unitNo', value: selectedOption })}
                                    options={unitList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">Unit Class</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.unitClass}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'unitClass', value: selectedOption })}
                                    options={unitClassList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">From</label>
                                <DatePicker
                                    selected={formData.from}
                                    onChange={(date) => handleDateChange({ name: 'from', value: date })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label w-0">To</label>
                                <DatePicker
                                    selected={formData.to}
                                    onChange={(date) => handleDateChange({ name: 'to', value: date })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100 datepicker-icon"
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">Move In</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.moveIn}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'moveIn', value: selectedOption })}
                                    options={boolOptions}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">Move Out</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.moveOut}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'moveOut', value: selectedOption })}
                                    options={boolOptions}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">Flat Shifting</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.flatShifting}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'flatShifting', value: selectedOption })}
                                    options={boolOptions}
                                />
                            </div>
                            <div className="d-flex mt-2 gap-1">
                                {loader === true ?
                                    <div className='mainbtnGry flex-end gap-2 w-50'>
                                        <ClipLoader color="#ffffff" className='mt-1' size={25} />
                                    </div>
                                    :
                                    <div
                                        onClick={() => getItems()}
                                        className="mainbtnGry flex-end gap-2 w-50"
                                    >
                                        <FaFileCircleExclamation
                                            color="#fff"
                                            className="center mt-2 mx-2"
                                        />
                                        <div className="center">Search</div>
                                    </div>
                                }
                                <div
                                    onClick={() => handleClearFilters()}
                                    className="mainbtnRed flex-end gap-2 w-50"
                                >
                                    <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                                    <div className="center">Reset</div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <div className="border-box2 p-3">
                {loading ? (
                    <div>Loading...</div>
                ) : (
                    <>
                        <div className="d-flex justify-content-between">
                            <h5 className="col mainInvHead">Lease Move In / Move Out Report</h5>
                            <button className="btn btn-primary" onClick={exportPDF}>
                                Export as PDF
                            </button>
                            <button className="btn btn-secondary mx-3" onClick={exportExcel}>
                                Export as Excel
                            </button>
                        </div>

                        <div className="gridshad mt-3">
                            <div className="ag-theme-quartz" style={{ height: 650 }}>
                                <AgGridReact
                                    ref={gridRef}
                                    getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: "#F0EDF5" } : { background: "#fff" })}
                                    rowData={rowData}
                                    enableFilter={true}
                                    floatingFilter={true}
                                    columnDefs={columnDefs}
                                />
                            </div>
                            <Pagination
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                pagination={pagination}
                                getItems={changePage}
                            />
                        </div>
                    </>
                )}
            </div>
        </div >
    );
};
export default LeaseMoveInOutReport;
